import styled from 'styled-components';
import {
  StickyHeaderModal,
  ModalTitleContainer,
  ModalTitleMain,
  ModalTitleCaption,
} from './common';
import Markdown from '../../Markdown';
import { SmallPortalTooltip } from '../../Tooltip';
import {
  TooltipContent as BaseTooltipContent,
  TooltipTitle,
  TooltipDetail,
} from '../Progress/Tooltip';
import { AssessmentTestResult } from '../../../api/admin';

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const ExplanationSection = styled.div`
  margin-bottom: 16px;
`;

const PlanItemsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ItemCard = styled.div<{ type: 'module' | 'project' }>`
  width: 100%;
  transition: all 0.2s ease-in-out;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.bgDefaultSecondary} 0%,
    ${({ theme, type }) =>
        type === 'module'
          ? theme.colors.buttonPrimary
          : theme.colors.highlight}08
      100%
  );
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};

  &:hover {
    border-color: ${({ theme, type }) =>
      type === 'module'
        ? theme.colors.buttonPrimary
        : theme.colors.highlight}40;
    transform: translateY(-1px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: stretch;
  gap: 16px;
`;

const IconWrapper = styled.div<{ type: 'module' | 'project' }>`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  flex-shrink: 0;
  align-self: flex-start;
  background-color: ${({ type, theme }) =>
    type === 'module' ? theme.colors.buttonPrimary : theme.colors.highlight}18;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const FallbackIcon = styled.i<{ type: 'module' | 'project' }>`
  color: ${({ type, theme }) =>
    type === 'module' ? theme.colors.buttonPrimary : theme.colors.highlight};
  font-size: 18px;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const TypeBadge = styled.span<{ type: 'module' | 'project' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ type, theme }) =>
    type === 'module' ? theme.colors.buttonPrimary : theme.colors.highlight};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const ItemTitle = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 16px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 2px;
`;

const MetaItem = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  display: flex;
  align-items: center;
  gap: 5px;

  i {
    font-size: 11px;
    opacity: 0.7;
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  flex-shrink: 0;
`;

const ObjectivesBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  background-color: ${({ theme }) => theme.colors.bgActive};
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textRegular};
  cursor: help;
  transition: all 0.2s ease;

  i {
    font-size: 12px;
    opacity: 0.6;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.bgActive}cc;
    color: ${({ theme }) => theme.colors.textActive};

    i {
      opacity: 1;
    }
  }
`;

const WhyThisLink = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textRegular};
  cursor: help;
  opacity: 0.6;
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    color: ${({ theme }) => theme.colors.buttonPrimary};
  }

  i {
    font-size: 10px;
  }
`;

const ObjectivesTooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 320px;
`;

const ObjectiveItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ObjectiveId = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textActive};
  font-family: monospace;
`;

const ObjectiveDescription = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  line-height: 1.4;
`;

const LearningRecommendationsModal = ({
  explanation,
  learningPlan,
  isOpen,
  onClose,
}: {
  explanation: string;
  learningPlan: AssessmentTestResult['learningRecommendations']['learningPlan'];
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <StickyHeaderModal
      title={
        <ModalTitleContainer>
          <ModalTitleMain>Learning Recommendations</ModalTitleMain>
          <ModalTitleCaption>
            Personalized suggestions for continued growth and development
          </ModalTitleCaption>
        </ModalTitleContainer>
      }
      onClose={onClose}
    >
      <ModalContentWrapper>
        <ExplanationSection>
          <Markdown md={explanation} />
        </ExplanationSection>

        <PlanItemsSection>
          {learningPlan.map((item, index) => {
            const displayTitle = item.title || item.slug;
            const iconUrl = item.unit?.iconUrl;
            const typeLabel = item.type === 'module' ? 'Module' : 'Project';
            const countText =
              item.type === 'module' ? '3 lessons' : '4 exercises';
            const objectiveCount = item.objectives.length;

            return (
              <ItemCard key={index} type={item.type}>
                <CardHeader>
                  <IconWrapper type={item.type}>
                    {iconUrl ? (
                      <CardIcon src={iconUrl} alt={displayTitle} />
                    ) : (
                      <FallbackIcon
                        type={item.type}
                        className={
                          item.type === 'module'
                            ? 'fa-solid fa-book-open'
                            : 'fa-solid fa-rocket'
                        }
                      />
                    )}
                  </IconWrapper>

                  <ContentSection>
                    <TypeBadge type={item.type}>{typeLabel}</TypeBadge>
                    <ItemTitle>{displayTitle}</ItemTitle>
                    <MetaRow>
                      <MetaItem>
                        <i
                          className={
                            item.type === 'module'
                              ? 'fa-solid fa-layer-group'
                              : 'fa-solid fa-code'
                          }
                        />
                        {countText}
                      </MetaItem>
                    </MetaRow>
                  </ContentSection>

                  <RightColumn>
                    <ObjectivesBadge className={`objectives-badge-${index}`}>
                      <i className="fa-solid fa-bullseye" />
                      {objectiveCount} objective
                      {objectiveCount !== 1 ? 's' : ''}
                    </ObjectivesBadge>
                    <WhyThisLink className={`why-this-${index}`}>
                      <i className="fa-regular fa-lightbulb" />
                      Why this {typeLabel.toLowerCase()}?
                    </WhyThisLink>
                  </RightColumn>
                </CardHeader>

                <SmallPortalTooltip anchorSelect={`.why-this-${index}`}>
                  <BaseTooltipContent>
                    <TooltipTitle>
                      Why this {typeLabel.toLowerCase()}?
                    </TooltipTitle>
                    <TooltipDetail>{item.explanation}</TooltipDetail>
                  </BaseTooltipContent>
                </SmallPortalTooltip>

                <SmallPortalTooltip anchorSelect={`.objectives-badge-${index}`}>
                  <BaseTooltipContent>
                    <TooltipTitle>Learning Objectives</TooltipTitle>
                    <ObjectivesTooltipList>
                      {item.objectives.map((objective, objIndex) => (
                        <ObjectiveItem key={objIndex}>
                          <ObjectiveId>{objective.objectiveId}</ObjectiveId>
                          {objective.description && (
                            <ObjectiveDescription>
                              {objective.description}
                            </ObjectiveDescription>
                          )}
                        </ObjectiveItem>
                      ))}
                    </ObjectivesTooltipList>
                  </BaseTooltipContent>
                </SmallPortalTooltip>
              </ItemCard>
            );
          })}
        </PlanItemsSection>
      </ModalContentWrapper>
    </StickyHeaderModal>
  );
};

export default LearningRecommendationsModal;
